import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Button,
  Alert,
  AlertTitle,
  CircularProgress
} from '@mui/material';
import { DownloadOutlined as DownloadIcon, Science as ScienceIcon } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { getJobFiles } from '@/api/jobs';
import { ModelType } from '@/types/colabfold';

interface Sequence {
  name: string;
  sequence: string;
  predicted_temp?: number;
  stability_score?: number;
}

interface OptimizationResultsProps {
  results: {
    wild_type_melting_temp?: number;
    target_melting_temp?: number;
    optimization_type?: 'manual' | 'ligandmpnn';
    sequences?: Sequence[];
  };
  jobId: string;
}

interface MutationResult {
  mutation: string;
  sequence: string;
  score?: number;
  ddg?: number;
  stability?: number;
  [key: string]: string | number | undefined;
}

export const OptimizationResults: React.FC<OptimizationResultsProps> = ({ results, jobId }) => {
  // Use the files API to get the actual files
  const { data: filesData, isLoading: filesLoading } = useQuery({
    queryKey: ['jobFiles', jobId, ModelType.PROTEINOPT],
    queryFn: () => getJobFiles(ModelType.PROTEINOPT, jobId),
  });

  // State for storing parsed CSV data
  const [csvData, setCsvData] = useState<{
    results: MutationResult[];
    headers: string[];
    isLoading: boolean;
  }>({
    results: [],
    headers: [],
    isLoading: true
  });

  // Process files data to extract CSV results
  useEffect(() => {
    const fetchCsvData = async () => {
      if (!filesData?.files) return;
      
      const outputFiles = filesData.files.filter((file: string) => 
        !file.startsWith('logs/') && file !== 'status.txt'
      );
      
      // Find the main output CSV file (looking for input_mutations.csv)
      const csvFile = outputFiles.find((file: string) => 
        file.endsWith('_input_mutations.csv') || file.endsWith('.csv')
      );
      
      if (!csvFile) {
        setCsvData(prev => ({ ...prev, isLoading: false }));
        return;
      }

      try {
        // Get direct download URL for the CSV file
        const getFileUrl = (filename: string) => {
          return `/api/proteinopt/jobs/${jobId}/download/?file=${filename}`;
        };

        const response = await fetch(getFileUrl(csvFile));
        const csvContent = await response.text();
        
        // Parse CSV content
        const lines = csvContent.trim().split('\n');
        if (lines.length < 2) {
          setCsvData(prev => ({ ...prev, isLoading: false }));
          return;
        }

        // Parse headers
        const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
        
        // Parse data rows
        const results: MutationResult[] = [];
        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(',').map(v => v.trim().replace(/"/g, ''));
          if (values.length >= headers.length) {
            const row: MutationResult = {
              mutation: '',
              sequence: ''
            };
            
            headers.forEach((header, index) => {
              const value = values[index];
              // Try to parse as number if it looks like one, otherwise keep as string
              if (!isNaN(Number(value)) && value !== '') {
                row[header.toLowerCase()] = Number(value);
              } else {
                row[header.toLowerCase()] = value;
              }
            });
            
            // Ensure we have required fields
            if (row.mutation || row.sequence) {
              results.push(row);
            }
          }
        }
        
        setCsvData({
          results,
          headers,
          isLoading: false
        });
      } catch (error) {
        console.error("Error fetching CSV data:", error);
        setCsvData(prev => ({ ...prev, isLoading: false }));
      }
    };
    
    if (filesData?.files) {
      fetchCsvData();
    }
  }, [filesData, jobId]);

  // Prefer API response, fallback to CSV data
  const wildTypeTemp = results.wild_type_melting_temp;
  const targetTemp = results.target_melting_temp;
  const optimizationType = results.optimization_type || 'ligandmpnn';

  const handleDownloadResults = () => {
    if (!csvData.results.length) return;
    
    // Create CSV content from the parsed data
    let csvContent = csvData.headers.join(',') + '\n';
    csvData.results.forEach(row => {
      const values = csvData.headers.map(header => {
        const value = row[header.toLowerCase()];
        return typeof value === 'string' && value.includes(',') ? `"${value}"` : value;
      });
      csvContent += values.join(',') + '\n';
    });
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('href', url);
    a.setAttribute('download', `protein-optimization-${jobId}.csv`);
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleSubmitForValidation = () => {
    alert('This feature is coming soon! This would allow you to submit these sequences for laboratory validation.');
  };

  // Show loading state
  if (filesLoading || csvData.isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading optimization results...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Protein Optimization Results
      </Typography>

      {/* Optimization Parameters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Optimization Parameters</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2">Optimization Type</Typography>
            <Typography variant="body1" gutterBottom>
              {optimizationType === 'manual' ? 'Manual Expert Review' : 'LigandMPNN Zero-Shot'}
            </Typography>
            {optimizationType === 'ligandmpnn' && (
              <Alert severity="info" sx={{ mt: 1 }}>
                LigandMPNN automatically generated optimized protein sequences using a machine learning approach.
              </Alert>
            )}
            {optimizationType === 'manual' && (
              <Alert severity="info" sx={{ mt: 1 }}>
                Your protein was reviewed by our protein engineering experts.
              </Alert>
            )}
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2">Temperature Settings</Typography>
            <Box>
              <Typography variant="body2" color="text.secondary">Wild Type Melting Temperature</Typography>
              <Typography variant="body1">
                {wildTypeTemp !== undefined && wildTypeTemp !== null ? `${wildTypeTemp} °C` : 'Not provided'}
              </Typography>
            </Box>
            <Box mt={2}>
              <Typography variant="body2" color="text.secondary">Target Melting Temperature</Typography>
              <Typography variant="body1">
                {targetTemp !== undefined && targetTemp !== null ? `${targetTemp} °C` : 'Not provided'}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Results Table */}
      {csvData.results.length > 0 && (
        <>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Optimization Results</Typography>
            <Box>
              <Button 
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadResults}
                sx={{ mr: 2 }}
              >
                Download CSV
              </Button>
              <Button 
                variant="contained" 
                color="primary"
                onClick={handleSubmitForValidation}
                startIcon={<ScienceIcon />}
              >
                Submit for Validation
              </Button>
            </Box>
          </Box>
          <Paper>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {csvData.headers.map((header, index) => (
                      <TableCell key={index}>
                        {header.charAt(0).toUpperCase() + header.slice(1).replace(/_/g, ' ')}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {csvData.results.map((row, index) => (
                    <TableRow key={index}>
                      {csvData.headers.map((header, colIndex) => {
                        const value = row[header.toLowerCase()];
                        const isSequence = header.toLowerCase().includes('sequence');
                        
                        return (
                          <TableCell key={colIndex}>
                            {isSequence ? (
                              <Tooltip title={value?.toString() || ''}>
                                <Typography sx={{ 
                                  maxWidth: 200, 
                                  overflow: 'hidden', 
                                  textOverflow: 'ellipsis', 
                                  whiteSpace: 'nowrap',
                                  fontFamily: 'monospace'
                                }}>
                                  {value?.toString() || '-'}
                                </Typography>
                              </Tooltip>
                            ) : (
                              <Typography>
                                {typeof value === 'number' 
                                  ? value.toFixed(value % 1 === 0 ? 0 : 2)
                                  : value?.toString() || '-'}
                              </Typography>
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}

      {csvData.results.length === 0 && !csvData.isLoading && (
        <Alert severity="info">
          <AlertTitle>No Results Yet</AlertTitle>
          The optimization is still in progress or no CSV results file has been generated yet.
        </Alert>
      )}
    </Box>
  );
}; 